import { useEffect, useState } from "react";
import {
  AnimatePresence,
  motion,
  useReducedMotion,
  useScroll,
  useSpring,
} from "framer-motion";
import { ArrowUp } from "lucide-react";
import { useLocation } from "react-router";

const EASE = [0.22, 1, 0.36, 1];
const SHOW_AFTER = 480;

function ScrollTop() {
  const reduce = useReducedMotion();
  const { pathname } = useLocation();
  const { scrollY, scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, {
    stiffness: reduce ? 400 : 120,
    damping: reduce ? 40 : 24,
    mass: 0.35,
  });
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const unsub = scrollY.on("change", (value) => setVisible(value > SHOW_AFTER));
    setVisible(scrollY.get() > SHOW_AFTER);
    return unsub;
  }, [scrollY, pathname]);

  const goTop = () => {
    const home = pathname === "/" ? document.getElementById("home") : null;
    const behavior = reduce ? "auto" : "smooth";
    if (home) {
      home.scrollIntoView({ behavior, block: "start" });
      return;
    }
    window.scrollTo({ top: 0, behavior });
  };

  return (
    <AnimatePresence>
      {visible ? (
        <motion.button
          type="button"
          onClick={goTop}
          aria-label="Back to top"
          className="group fixed bottom-6 right-6 z-40 flex h-14 w-14 items-center justify-center bg-navy text-gold shadow-[0_18px_44px_-16px_rgb(var(--color-navy)/0.6)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 sm:bottom-8 sm:right-8"
          initial={reduce ? { opacity: 1 } : { opacity: 0, y: 18, scale: 0.86 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={reduce ? { opacity: 0 } : { opacity: 0, y: 14, scale: 0.88 }}
          whileHover={reduce ? undefined : { y: -4, scale: 1.05 }}
          whileTap={reduce ? undefined : { scale: 0.92, y: 0 }}
          transition={reduce ? { duration: 0 } : { duration: 0.38, ease: EASE }}
        >
          <svg
            className="pointer-events-none absolute inset-0 h-full w-full -rotate-90"
            viewBox="0 0 56 56"
            aria-hidden="true"
          >
            <rect
              x="3"
              y="3"
              width="50"
              height="50"
              fill="none"
              className="stroke-gold/25 transition-colors duration-300 group-hover:stroke-navy/20"
              strokeWidth="2.5"
            />
            <motion.rect
              x="3"
              y="3"
              width="50"
              height="50"
              fill="none"
              className="stroke-gold transition-colors duration-300 group-hover:stroke-navy"
              strokeWidth="2.5"
              strokeLinecap="square"
              pathLength={1}
              style={{ pathLength: progress }}
            />
          </svg>

          <span className="absolute inset-0 bg-gold/0 transition-colors duration-300 group-hover:bg-gold" />

          <motion.span
            className="relative z-[1] text-gold transition-colors duration-300 group-hover:text-navy"
            animate={reduce ? undefined : { y: [0, -3, 0] }}
            transition={
              reduce
                ? { duration: 0 }
                : { duration: 1.7, repeat: Infinity, ease: "easeInOut" }
            }
          >
            <ArrowUp className="h-5 w-5" strokeWidth={2.2} aria-hidden="true" />
          </motion.span>
        </motion.button>
      ) : null}
    </AnimatePresence>
  );
}

export default ScrollTop;
